import * as baseService from "./base";

let workEventId;
let workItemId;
let stepNum;
let configuration;
let authToken;
let organization;
let application;
let platConfig;
let mode;
let status;

export const getInfo = () => {
  return Object.freeze({
    workEventId,
    workItemId,
    stepNum,
    configuration,
    authToken,
    organization,
    application,
    mode,
    status,
  });
};

// create an Observer instance
// https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver/observe
const resizeObserver = new ResizeObserver((entries) =>
  window.top.postMessage(
    {
      messageType: "HEIGHT",
      message: {
        stepNum: stepNum,
        workItemId: workItemId,
        height: entries[0].target.scrollHeight,
      },
    },
    "*"
  )
);

export async function initialize(listener) {
  return new Promise((resolve, reject) => {
    try {
      window.top.postMessage(
        {
          messageType: "READY",
        },
        "*"
      );

      const handleMessage = async (event) => {
        const messageType = event.data?.messageType;
        const messageData = event.data?.message;
        switch (messageType) {
          case "INITIALIZATION":
            workEventId = messageData.workEventId;
            workItemId = messageData.workItemId;
            stepNum = messageData.stepNumber;
            configuration = messageData.configuration;
            authToken = messageData.authToken;
            organization = messageData.organization;
            application = messageData.application;
            platConfig = messageData.platConfig;
            mode = messageData.mode;
            status = messageData.status;

            event.source.postMessage(
              {
                messageType: "HEIGHT",
                message: {
                  stepNum: stepNum,
                  workItemId: workItemId,
                  height: document.body.scrollHeight,
                },
              },
              event.origin
            );
            resizeObserver.observe(document.body);
            resolve();
            break;
          case "STATUS":
            status = messageData.status;
            if (listener && typeof listener === "function") {
              listener(status);
            }
            break;
          case "AUTH_TOKEN":
            authToken = messageData.authToken;
            break;
          default:
            break;
        }
      };
      window.addEventListener("message", handleMessage);
    } catch (error) {
      reject(error);
    }
  });
}

resizeObserver.observe(document.body);

/**
 * Saves a result file for the procedure step.
 *
 * @param {(Blob|File)} data  The file contents.
 * @param {string} [name]  The saved filename.  If not supplied, will use File.name property.
 * @param {string} [type]  the MIME type.  If not supplied will use the Blob.type or File.type property.
 */
export async function putFile(data, name, type) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/steps/${stepNum}/resulturls/put/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let { url } = await response.json();
    const blobResponse = await baseService.makeFetch(url, {
      method: "PUT",
      body: data,
      headers: new Headers({
        "Content-Type": type || "binary/octet-stream",
      }),
    });
    return blobResponse.statusText;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Get Method for Work Event Item Step Result (PUT)
 * @param {string} [name]
 * @returns {{url: string}}
 */
export async function getSecurePutUrl(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/steps/${stepNum}/resulturls/put/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Get Method for Work Event Item Step Result (GET)
 * @param {string} [name]
 * @returns {{url: string}}
 */
export async function getSecureGetUrl(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/steps/${stepNum}/resulturls/get/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Retrievea a result file for the procedure step as a Blob.
 *
 * @param {string} name  The name of the file to retrieve.
 * @returns {Blob}
 */
export async function getFileAsBlob(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/steps/${stepNum}/resulturls/get/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let { url } = await response.json();

    const blobResponse = await baseService.makeFetch(url, {
      method: "GET",
      headers: new Headers({}),
    });
    return blobResponse.blob();
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Retrievea a result JSON file for the procedure step as an Object.
 *
 * @param {string} name  The name of the file to retrieve.
 * @returns {Object}
 */
export async function getFileAsObject(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/steps/${stepNum}/resulturls/get/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let { url } = await response.json();
    const objectResponse = await baseService.makeFetch(url, {
      method: "GET",
      headers: new Headers({}),
    });
    if (objectResponse.ok) {
      let contentType = objectResponse.headers.get("Content-Type");

      if (contentType?.indexOf("application/json") > -1) {
        return objectResponse.json();
      }

      return response.statusText;
    }
    throw response;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Deletes a result file for the procedure step.
 *
 * @param {string} name  The name of the file to delete.
 */
export async function deleteFile(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/steps/${stepNum}/resulturls/delete/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let { url } = await response.json();
    return baseService.destroy(url);
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Lists names of existing result files for the procedure step.
 *
 * @returns {string[]}
 */
export async function listFiles() {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/steps/${stepNum}/resultkeys`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Sets the procedure step status
 */
export async function setStatus(
  status = null,
  result_code = null,
  result_qualifier = null
) {
  let URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/steps/${stepNum}/status`;
  let response = await baseService.makeFetch(URL, {
    method: "PUT",
    body: JSON.stringify({ status, result_code, result_qualifier }),
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
      "Content-Type": "application/json",
    }),
  });
  if (response.ok) {
    let json = await response.json();
    window.top.postMessage(
      {
        messageType: "STATUS",
        message: json,
      },
      "*"
    );
    return;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

export async function getStatus() {
  let URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/steps/${stepNum}/status`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
      "Content-Type": "application/json",
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Marks the procedure step as STARTED.
 */
export async function setCompleted(
  result_code = null,
  result_qualifier = null
) {
  setStatus("COMPLETED", result_code, result_qualifier);
}

/**
 * Marks the procedure step as COMPLETED.
 */
export async function setStarted(result_code = null, result_qualifier = null) {
  setStatus("STARTED", result_code, result_qualifier);
}

/**
 * Adds or updates the specified result attribute for the work item.
 */
export async function setResultAttribute(name, value) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/resultattributes/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "PUT",
    body: JSON.stringify({ value }),
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
      "Content-Type": "application/json",
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Gets the value of a result attribute for the work item.
 *
 * @returns {string}
 */
export async function getResultAttribute(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/resultattributes/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Deletes a result attribute for the work item.
 */
export async function deleteResultAttribute(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/resultattributes/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "DELETE",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Adds or updates the specified result attribute for the work event.
 */
export async function setWorkEventAttribute(name, value) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/attributes/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "PUT",
    body: JSON.stringify({ value }),
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
      "Content-Type": "application/json",
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Gets the value of a result attribute for the work event.
 *
 * @returns {string}
 */
export async function getWorkEventAttribute(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/attributes/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Deletes a result attribute for the work event.
 */
export async function deleteWorkEventAttribute(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/attributes/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "DELETE",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Adds or updates the specified result attribute for the work item.
 */
export async function setWorkItemAttribute(name, value) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/attributes/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "PUT",
    body: JSON.stringify({ value }),
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
      "Content-Type": "application/json",
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Gets the value of a result attribute for the work item.
 *
 * @returns {string}
 */
export async function getWorkItemAttribute(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/attributes/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "GET",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}

/**
 * Deletes a result attribute for the work event.
 */
export async function deleteWorkItemAttribute(name) {
  const URL = `${platConfig.baseURL}/workevents/${workEventId}/workitems/${workItemId}/attributes/${name}`;
  let response = await baseService.makeFetch(URL, {
    method: "DELETE",
    headers: new Headers({
      Authorization: authToken,
      Application: application,
      Organization: organization,
    }),
  });
  if (response.ok) {
    let json = await response.json();
    return json;
  } else if (response.status === 401) {
    let json = await response.json();
    throw json;
  }
}
